import { z } from 'zod'
import { DynamoStartKeySchema } from '../paging'
import { AccountIdSchema, CardAttachmentIdSchema, CardSendIdSchema } from '../ids'
import { ALLOWED_ATTACHMENT_MIME_TYPES } from '../attachment'
import { apiRequest, apiResponses } from '../api'
import { isDefined } from '../../util/isEmpty'
import { PublicUserSchema, UserCardSchema } from './userCard'
import { CardActivitySchema, DecoratedCardSchema } from './model'
import { CompanyCardSchema, PublicCompanySchema } from './companyCard'
import { CardEntityIdSchema, CardGalleryFileSchema, CardResidentSchema, CardSlugSchema } from './common'

const AttachmentMimeTypeSchema = z.enum(ALLOWED_ATTACHMENT_MIME_TYPES)

/**
 * /card/get
 */
export const CardGetInputSchema = apiRequest({
  entityId: CardEntityIdSchema,
})
export const [CardGetSuccessSchema, CardGetOutputSchema] = apiResponses({
  card: DecoratedCardSchema,
})
export type CardGetInput = z.infer<typeof CardGetInputSchema>
export type CardGetSuccess = z.infer<typeof CardGetSuccessSchema>
export type CardGetOutput = z.infer<typeof CardGetOutputSchema>

/**
 * /card/activity — activity for the current user's card
 */
export const CardActivityInputSchema = apiRequest({
  size: z.number().int().positive().max(100).optional(),
  cursor: DynamoStartKeySchema.optional(),
})
export const [CardActivitySuccessSchema, CardActivityOutputSchema] = apiResponses({
  activity: z.array(CardActivitySchema),
  cursor: DynamoStartKeySchema.optional(),
})
export type CardActivityInput = z.infer<typeof CardActivityInputSchema>
export type CardActivitySuccess = z.infer<typeof CardActivitySuccessSchema>
export type CardActivityOutput = z.infer<typeof CardActivityOutputSchema>

/**
 * /card/update
 */
const CompanyCardUpdateSchema = CompanyCardSchema.pick({
  entityType: true,
  entityId: true,
  slug: true,
  links: true,
  attachmentIds: true,
  theme: true,
  sections: true,
  imageSource: true,
  maxImagesPerCard: true,
  starThreshold: true,
  teamInboxEmail: true,
  notifyManager: true,
  messageTemplates: true,
  placeId: true,
})

const UserCardUpdateSchema = UserCardSchema.pick({
  entityType: true,
  entityId: true,
  slug: true,
  prefix: true,
})

export const CardUpdateInputSchema = apiRequest({
  card: z.discriminatedUnion('entityType', [CompanyCardUpdateSchema, UserCardUpdateSchema]),
})
export const [CardUpdateSuccessSchema, CardUpdateOutputSchema] = apiResponses({
  card: DecoratedCardSchema,
})
export type CardUpdateInput = z.infer<typeof CardUpdateInputSchema>
export type CardUpdateSuccess = z.infer<typeof CardUpdateSuccessSchema>
export type CardUpdateOutput = z.infer<typeof CardUpdateOutputSchema>

/**
 * /card/send
 */
export const CardSendInputSchema = apiRequest({
  accountId: AccountIdSchema.optional(),
  attachmentIds: z.array(CardAttachmentIdSchema),
  resident: CardResidentSchema.optional(),
})
export const [CardSendSuccessSchema, CardSendOutputSchema] = apiResponses({
  sendId: CardSendIdSchema,
})
export type CardSendInput = z.infer<typeof CardSendInputSchema>
export type CardSendSuccess = z.infer<typeof CardSendSuccessSchema>
export type CardSendOutput = z.infer<typeof CardSendOutputSchema>

/**
 * /card/sent
 */
export const CardSentInputSchema = apiRequest({
  sendId: CardSendIdSchema,
})
export const [CardSentSuccessSchema, CardSentOutputSchema] = apiResponses({})
export type CardSentInput = z.infer<typeof CardSentInputSchema>
export type CardSentSuccess = z.infer<typeof CardSentSuccessSchema>
export type CardSentOutput = z.infer<typeof CardSentOutputSchema>

const CardIdentifierSchema = z.object({
  slug: CardSlugSchema.optional(),
  sendId: CardSendIdSchema.optional(),
})

/**
 * /card/action
 */
export const CardActionInputSchema = apiRequest({
  action: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('view'),
      ...CardIdentifierSchema.shape,
    }),
    z.object({
      type: z.literal('save'),
      ...CardIdentifierSchema.shape,
    }),
    z.object({
      type: z.literal('review'),
      ...CardIdentifierSchema.shape,
      feedback: z.string().trim().min(1).max(2000),
    }),
  ]),
}).refine(({ action }) => isDefined(action.slug) || isDefined(action.sendId), {
  message: 'Either slug or sendId is required',
})
export const [CardActionSuccessSchema, CardActionOutputSchema] = apiResponses({})
export type CardActionInput = z.infer<typeof CardActionInputSchema>
export type CardActionSuccess = z.infer<typeof CardActionSuccessSchema>
export type CardActionOutput = z.infer<typeof CardActionOutputSchema>

/**
 * /card/attachment
 */
export const CardAttachmentAddInputSchema = apiRequest({
  contentType: AttachmentMimeTypeSchema.extract([
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/heic',
    'image/heif',
    'image/tiff',
    'image/bmp',
  ]),
  contentLength: z
    .int()
    .min(1)
    .max(8 * 1024 * 1024),
})
export const [CardAttachmentAddSuccessSchema, CardAttachmentAddOutputSchema] = apiResponses({
  uploadUrl: z.string(),
})
export type CardAttachmentAddInput = z.infer<typeof CardAttachmentAddInputSchema>
export type CardAttachmentAddSuccess = z.infer<typeof CardAttachmentAddSuccessSchema>
export type CardAttachmentAddOutput = z.infer<typeof CardAttachmentAddOutputSchema>

/**
 * /card/{slug}
 */
export const CardBySlugParamsSchema = z.object({
  slug: CardSlugSchema,
})
export const [CardBySlugSuccessSchema, CardBySlugOutputSchema] = apiResponses({
  company: PublicCompanySchema,
  user: PublicUserSchema.optional(),
  gallery: z.array(CardGalleryFileSchema),
})
export type CardBySlugParams = z.infer<typeof CardBySlugParamsSchema>
export type CardBySlugSuccess = z.infer<typeof CardBySlugSuccessSchema>
export type CardBySlugOutput = z.infer<typeof CardBySlugOutputSchema>
